// src/components/managerdashboard/TeamOverviewPanel.jsx
//
// Per-member team overview grid.
// Replaces the previous counts-only attendance bar chart.

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, Plane, Home, Calendar, Target, AlertTriangle, BookOpen, Filter } from "lucide-react";
import { Skeleton, ErrorState, EmptyState } from "../ui/LoadingStates";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const NEON   = "#00ff90";
const AMBER  = "#fbbf24";
const RED    = "#f87171";
const BLUE   = "#60a5fa";
const MUTED  = "rgba(255,255,255,0.35)";

const STATUS_META = {
  "Present":  { color: NEON,  icon: Check,    label: "Present" },
  "WFH":      { color: BLUE,  icon: Home,     label: "WFH"     },
  "On Leave": { color: AMBER, icon: Plane,    label: "On Leave" },
  "Absent":   { color: RED,   icon: X,        label: "Absent"  },
  "Not in":   { color: MUTED, icon: AlertTriangle, label: "Not in" },
};

const FILTERS = [
  { key: "all",     label: "All"      },
  { key: "Present", label: "Present"  },
  { key: "Absent",  label: "Absent"   },
  { key: "On Leave",label: "On Leave" },
];

function initials(name) {
  return (name || "?").split(/\s+/).map(p => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

export default function TeamOverviewPanel({ managerId }) {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [filter,  setFilter]  = useState("all");

  // Open the staff profile route. `/profile/:userId` is the staff-side
  // view that's accessible to managers as well (ProtectedRoute, not
  // role-gated). `/admin/profile/:userId` is the admin-only variant.
  const openMember = (m) => {
    if (!m?.id) return;
    navigate(`/profile/${m.id}`);
  };

  const load = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    setLoading(true); setError(null);
    fetch(`${API_BASE}/api/manager/team/overview`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => {
        const list = d?.data?.members || d?.members || (Array.isArray(d) ? d : []);
        setMembers(list);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return members;
    return members.filter(m => (m.today_status || "").toLowerCase() === filter.toLowerCase());
  }, [members, filter]);

  const counts = useMemo(() => {
    const c = { all: members.length, Present: 0, Absent: 0, "On Leave": 0 };
    members.forEach(m => {
      const s = m.today_status;
      if (s === "Present" || s === "WFH") c.Present += 1;
      if (s === "Absent") c.Absent += 1;
      if (s === "On Leave") c["On Leave"] += 1;
    });
    return c;
  }, [members]);

  return (
    <div className="col-span-1 md:col-span-2 lg:col-span-3 rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022] p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Team Overview</span>
          {!loading && !error && (
            <span className="text-xs text-white/30">· {members.length} member{members.length !== 1 ? "s" : ""}</span>
          )}
        </div>

        {/* Filter bar */}
        <div className="flex gap-1 items-center" role="tablist">
          <Filter size={12} style={{ color: MUTED, marginRight: 4 }} />
          {FILTERS.map(f => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                role="tab"
                aria-selected={active}
                onClick={() => setFilter(f.key)}
                className="text-xs px-3 py-1 rounded-full border transition-colors"
                style={{
                  borderColor: active ? `${NEON}66` : "rgba(255,255,255,0.08)",
                  background:  active ? `${NEON}14` : "transparent",
                  color:       active ? NEON : "rgba(255,255,255,0.45)",
                }}
              >
                {f.label}{f.key !== "all" ? ` · ${counts[f.key] ?? 0}` : ` · ${counts.all ?? 0}`}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <SkeletonGridLoader />
      ) : error ? (
        <ErrorState message="Couldn't load team overview" detail={error} onRetry={load} />
      ) : members.length === 0 ? (
        <EmptyState
          title="No team members yet"
          body="When team members are assigned to you, they'll show up here."
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          title={`No one matches "${filter}"`}
          body="Try a different filter to see other team members."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(m => <MemberCard key={m.id} m={m} onOpen={openMember} />)}
        </div>
      )}
    </div>
  );
}

function MemberCard({ m, onOpen }) {
  const meta = STATUS_META[m.today_status] || STATUS_META["Not in"];
  const StatusIcon = meta.icon;
  const onTrack = m.goals_on_track ?? 0;
  const overdue = m.goals_overdue ?? 0;
  const training = m.training_overdue ?? 0;
  const leaveLeft = m.leave_remaining;

  return (
    <div
      className="rounded-xl bg-white/3 p-4"
      onClick={() => onOpen?.(m)}
      onMouseOver={(e) => { e.currentTarget.style.borderColor = "#1e1e1e"; }}
      onMouseOut={(e)  => { e.currentTarget.style.borderColor = "#111"; }}
      style={{
        display: "flex", flexDirection: "column", gap: 12,
        cursor: "pointer",
        border: "1px solid #111",
        transition: "border-color 0.15s",
      }}
    >
      {/* Header — avatar + name + status */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {m.avatar ? (
          <img
            src={m.avatar}
            alt={m.name}
            style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
          />
        ) : (
          <div
            style={{
              width: 40, height: 40, borderRadius: "50%",
              background: "rgba(0,255,144,0.1)",
              border: "0.5px solid rgba(0,255,144,0.25)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: NEON, fontSize: 13, fontWeight: 600, flexShrink: 0,
            }}
          >
            {initials(m.name)}
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 500, color: "#fff",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {m.name}
          </div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>
            {m.role}{m.department && m.department !== "-" ? ` · ${m.department}` : ""}
          </div>
        </div>

        {/* Status pill */}
        <div
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            fontSize: 11, padding: "4px 10px", borderRadius: 999,
            background: `${meta.color}1a`, color: meta.color,
            border: `0.5px solid ${meta.color}33`, flexShrink: 0,
          }}
        >
          <StatusIcon size={11} />
          {meta.label}
        </div>
      </div>

      {/* Metrics row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <MetricCell
          icon={Calendar}
          color={NEON}
          label="Leave left"
          value={leaveLeft == null ? "—" : `${leaveLeft}d`}
        />
        <MetricCell
          icon={Target}
          color={overdue > 0 ? AMBER : NEON}
          label="Goals"
          value={`${onTrack} on track${overdue > 0 ? ` · ${overdue} overdue` : ""}`}
        />
        <MetricCell
          icon={BookOpen}
          color={training > 0 ? AMBER : MUTED}
          label="Training"
          value={training > 0 ? `${training} overdue` : "On track"}
          span={2}
        />
      </div>
    </div>
  );
}

function MetricCell({ icon: Icon, color, label, value, span = 1 }) {
  return (
    <div
      style={{
        gridColumn: `span ${span}`,
        background: "rgba(255,255,255,0.02)",
        border: "0.5px solid rgba(255,255,255,0.05)",
        borderRadius: 8,
        padding: "8px 10px",
        display: "flex", alignItems: "center", gap: 8,
      }}
    >
      <Icon size={13} style={{ color, flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: 1 }}>
          {label}
        </div>
        <div style={{ fontSize: 11, color: "#fff", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {value}
        </div>
      </div>
    </div>
  );
}

function SkeletonGridLoader() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <div key={i} className="rounded-xl border border-white/8 bg-white/3 p-4"
             style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Skeleton width={40} height={40} radius="50%" />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              <Skeleton width="60%" height={12} />
              <Skeleton width="40%" height={9} />
            </div>
            <Skeleton width={70} height={18} radius={999} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <Skeleton height={36} radius={8} />
            <Skeleton height={36} radius={8} />
            <div style={{ gridColumn: "span 2" }}><Skeleton height={36} radius={8} /></div>
          </div>
        </div>
      ))}
    </div>
  );
}
